import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FilePdfIcon as FilePdf, ShieldCheckIcon as ShieldCheck, MagnifyingGlassIcon as MagnifyingGlass, VideoCameraIcon as VideoCamera, DownloadSimpleIcon as DownloadSimple, ShareIcon as Share, CopySimpleIcon as CopySimple, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * /admin/launch-pack — single admin page that surfaces:
 *   1. Business Plan (download + share copy-link)
 *   2. Insurance & Legal Bundle (preview + download + email-to-counsel)
 *   3. SEO inventory (read-only, with one-click links to public validators)
 *   4. Sample-tour video URL (admin can paste a URL that surfaces on
 *      the public /video-tours landing page)
 *
 * The page is read-mostly. The only writeable surface is the sample-tour
 * URL field, which writes to platform_settings via PUT.
 */
const Section = ({ icon: Icon, label, color, children, testId }) => (
  <section
    className="rounded-2xl border border-[#E5E5E5] bg-white p-6 mb-5"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-4">
      <span
        className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}15`, color }}
      >
        <Icon size={20} weight="duotone" />
      </span>
      <div>
        <h2 className="font-spectral text-2xl tracking-tight">{label}</h2>
      </div>
    </div>
    {children}
  </section>
);

export default function AdminLaunchPack() {
  const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

  // Business Plan
  const [bpMeta, setBpMeta] = useState(null);

  // Insurance bundle
  const [bundle, setBundle] = useState(null);
  const [bundleLoading, setBundleLoading] = useState(false);

  // SEO
  const [seo, setSeo] = useState(null);

  // Sample tour
  const [sample, setSample] = useState({ url: "", poster_url: "", caption: "" });
  const [sampleSaving, setSampleSaving] = useState(false);

  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/admin/launch-pack/business-plan")
      .then((r) => setBpMeta(r.data))
      .catch(() => setBpMeta({ missing: true }));
    api.get("/admin/launch-pack/seo")
      .then((r) => setSeo(r.data))
      .catch(() => {});
    api.get("/admin/launch-pack/sample-tour")
      .then((r) => setSample({
        url: r.data.url || "",
        poster_url: r.data.poster_url || "",
        caption: r.data.caption || "",
      }))
      .catch(() => {});
  }, []);

  const loadBundle = async () => {
    setBundleLoading(true);
    try {
      const r = await api.get("/admin/launch-pack/insurance-bundle");
      setBundle(r.data);
    } catch (e) {
      toast.error("Couldn't generate the bundle.");
    } finally {
      setBundleLoading(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — clipboard blocked by the browser.");
    }
  };

  const downloadAuthed = async (path, fallbackName) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      // Try to honour the server's Content-Disposition header.
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download — check your admin session.");
    }
  };

  const saveSample = async () => {
    setSampleSaving(true);
    try {
      await api.put("/admin/launch-pack/sample-tour", sample);
      toast.success("Saved. The /video-tours page now surfaces this.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSampleSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-gold" />
          Admin · Launch pack
        </p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">
          Business plan, insurance dossier &amp; SEO
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Everything you'd hand to a lawyer, an insurance broker, an
          investor, or a journalist — in one place. All artefacts are
          downloadable and shareable.
        </p>
      </div>

      {/* Business Plan */}
      <Section
        icon={FilePdf}
        label="Business plan"
        color="#3F4A8A"
        testId="lp-business-plan"
      >
        <p className="text-[13.5px] text-[#5C5C5C] leading-relaxed mb-4 max-w-2xl">
          Operating doc with funding-context appendix. Covers product
          inventory, market positioning, conservative + aggressive 3-year
          revenue projections, prioritised roadmap, risks, and unit
          economics. Markdown — open in any editor or viewer.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => downloadAuthed("/admin/launch-pack/business-plan/download.pdf", "armstrong-business-plan.pdf")}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#3F4A8A] text-white"
            data-testid="lp-bp-download-pdf"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Download PDF
          </Button>
          <Button
            onClick={() => downloadAuthed("/admin/launch-pack/business-plan/download", "armstrong-business-plan.md")}
            variant="outline"
            className="rounded-full border-[#3F4A8A] text-[#3F4A8A] hover:bg-[#3F4A8A] hover:text-white"
            data-testid="lp-bp-download"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            .md (raw)
          </Button>
          <button
            type="button"
            onClick={() => copy(`${apiBase}/api/admin/launch-pack/business-plan/download.pdf`, "bp-link")}
            className="text-[12.5px] text-[#3F4A8A] font-bold hover:underline inline-flex items-center gap-1"
            data-testid="lp-bp-copy-link"
          >
            {copied === "bp-link" ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><CopySimple size={12} weight="bold" /> Copy PDF share-link</>}
          </button>
          <a
            href={`mailto:?subject=${encodeURIComponent("Armstrong K9 Registry — Business Plan")}&body=${encodeURIComponent("Attached: business plan covering product inventory, market positioning, projections, roadmap.\n\n— Founder, Armstrong K9 Registry")}`}
            className="text-[12.5px] text-[#0F8043] font-bold hover:underline inline-flex items-center gap-1"
            data-testid="lp-bp-email"
          >
            <Share size={12} weight="bold" /> Email
          </a>
          {bpMeta && !bpMeta.missing && (
            <span className="text-[11.5px] text-[#9CA3AF]">
              {bpMeta.lines} lines · {Math.round(bpMeta.size_bytes / 1024)} KB · updated {bpMeta.updated_at && new Date(bpMeta.updated_at).toLocaleDateString()}
            </span>
          )}
          {bpMeta?.missing && (
            <span className="text-[12px] text-crimson font-bold">
              Business plan not on disk — contact engineering to publish.
            </span>
          )}
        </div>
        <p className="text-[11.5px] text-[#9CA3AF] mt-3">
          The share-link requires admin authentication; it's safe to send
          to a co-founder or counsel who has admin access. To share with an
          investor without giving them admin access, download the file and
          attach it to email.
        </p>
      </Section>

      {/* Insurance & Legal Bundle */}
      <Section
        icon={ShieldCheck}
        label="Insurance & legal dossier"
        color="#0F8043"
        testId="lp-insurance-bundle"
      >
        <p className="text-[13.5px] text-[#5C5C5C] leading-relaxed mb-4 max-w-2xl">
          Auto-generated dossier of every consent the platform collects,
          every protection it provides to buyers + sellers, and every
          security control. Designed to be handed to an insurance
          underwriter or your business attorney without further editing.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {!bundle ? (
            <Button
              onClick={loadBundle}
              disabled={bundleLoading}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white"
              data-testid="lp-ib-preview"
            >
              <MagnifyingGlass size={14} weight="bold" className="mr-1.5" />
              {bundleLoading ? "Generating…" : "Preview the dossier"}
            </Button>
          ) : (
            <span className="text-[12px] text-[#0F8043] font-bold inline-flex items-center gap-1.5" data-testid="lp-ib-loaded">
              <CheckCircle size={12} weight="fill" /> Loaded — preview below
            </span>
          )}
          <Button
            onClick={() => downloadAuthed("/admin/launch-pack/insurance-bundle/download.pdf", "armstrong-insurance-legal-bundle.pdf")}
            className="rounded-full bg-[#0F8043] hover:bg-[#0E0E0E] text-white"
            data-testid="lp-ib-download-pdf"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Download PDF
          </Button>
          <Button
            onClick={() => downloadAuthed("/admin/launch-pack/insurance-bundle/download", "armstrong-insurance-legal-bundle.txt")}
            variant="outline"
            className="rounded-full border-[#0F8043] text-[#0F8043] hover:bg-[#0F8043] hover:text-white"
            data-testid="lp-ib-download"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            .txt (raw)
          </Button>
          <a
            href={`mailto:?subject=${encodeURIComponent("Armstrong K9 Registry — Insurance & Legal Dossier")}&body=${encodeURIComponent("The dossier covering platform consents, buyer/seller protections, and security controls is attached. Please review at your earliest convenience.\n\n— Founder, Armstrong K9 Registry")}`}
            className="rounded-full border border-[#0F8043] text-[#0F8043] hover:bg-[#0F8043] hover:text-white px-4 h-9 text-[12.5px] font-bold inline-flex items-center gap-1.5 transition-colors"
            data-testid="lp-ib-email"
          >
            <Share size={12} weight="bold" />
            Email to counsel
          </a>
        </div>
        {bundle && (
          <div className="mt-3">
            <Textarea
              readOnly
              value={bundle.content}
              className="h-72 font-mono text-[11px] leading-tight"
              data-testid="lp-ib-preview-text"
            />
            <p className="text-[11.5px] text-[#9CA3AF] mt-2">
              {Math.round(bundle.size_bytes / 1024)} KB · generated {new Date(bundle.generated_at).toLocaleString()}
            </p>
          </div>
        )}
      </Section>

      {/* SEO */}
      <Section
        icon={MagnifyingGlass}
        label="SEO &amp; social-share inventory"
        color="#C9A227"
        testId="lp-seo"
      >
        {!seo ? (
          <p className="text-[13.5px] text-[#9CA3AF]">Loading…</p>
        ) : (
          <>
            <p className="text-[13.5px] text-[#5C5C5C] leading-relaxed mb-4 max-w-2xl">
              Read-only inventory of the metadata served on the public
              <code className="text-[12px] mx-1 px-1.5 py-0.5 bg-[#FAFAFA] rounded border border-[#E5E5E5]">/video-tours</code>
              landing page. To edit any value, modify
              <code className="text-[12px] mx-1 px-1.5 py-0.5 bg-[#FAFAFA] rounded border border-[#E5E5E5]">VideoToursLanding.jsx</code>
              and redeploy — versioning is intentional.
            </p>
            <div className="rounded-xl bg-[#FAFAFA] border border-[#E5E5E5] p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-[12.5px] mb-4" data-testid="lp-seo-vt-inventory">
              <SeoRow label="Page URL" value={seo.video_tours.page_url} link />
              <SeoRow label="Title" value={seo.video_tours.title} />
              <SeoRow label="Description" value={seo.video_tours.description} />
              <SeoRow label="OG image" value={seo.video_tours.og_image} link />
              <SeoRow label="JSON-LD blocks" value={seo.video_tours.json_ld_blocks.join(", ")} />
              <SeoRow label="OG type" value={seo.static_defaults.og_type} />
              <SeoRow label="OG site name" value={seo.static_defaults.og_site_name} />
              <SeoRow label="Twitter card" value={seo.static_defaults.twitter_card} />
              <SeoRow label="Twitter handle" value={seo.static_defaults.twitter_site} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00] mb-2">
                Validators (use after a deploy)
              </p>
              <div className="flex flex-wrap gap-2" data-testid="lp-seo-validators">
                {seo.video_tours.validators.map((v) => (
                  <a
                    key={v.label}
                    href={v.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-[#E5E5E5] px-3 py-1.5 text-[12px] text-ink hover:border-[#C9A227] hover:bg-[#FBF7EC] inline-flex items-center gap-1.5 transition-colors"
                    data-testid={`lp-seo-validator-${v.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {v.label}
                    <ArrowSquareOut size={11} className="text-[#9CA3AF]" />
                  </a>
                ))}
              </div>
            </div>
          </>
        )}
      </Section>

      {/* Sample tour */}
      <Section
        icon={VideoCamera}
        label="Sample tour video"
        color="#C1272D"
        testId="lp-sample-tour"
      >
        <p className="text-[13.5px] text-[#5C5C5C] leading-relaxed mb-4 max-w-2xl">
          Upload a short (~60–90 second) recorded walkthrough demonstrating
          a video tour and paste its URL below. The
          <code className="text-[12px] mx-1 px-1.5 py-0.5 bg-[#FAFAFA] rounded border border-[#E5E5E5]">/video-tours</code>
          public landing page will surface a Sample-Tour player section
          right under the hero, increasing booking conversion. Leave the
          field blank to hide the section.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="lp-sample-url">Video URL</Label>
            <Input
              id="lp-sample-url"
              value={sample.url}
              onChange={(e) => setSample({ ...sample, url: e.target.value })}
              placeholder="https://armstrong.daily.co/recordings/... or a YouTube embed URL"
              data-testid="lp-sample-url"
            />
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              MP4/WebM hosted anywhere with public read, or a <code>https://www.youtube.com/embed/&lt;id&gt;</code> URL.
            </p>
          </div>
          <div>
            <Label htmlFor="lp-sample-poster">Poster image (optional)</Label>
            <Input
              id="lp-sample-poster"
              value={sample.poster_url}
              onChange={(e) => setSample({ ...sample, poster_url: e.target.value })}
              placeholder="https://..."
              data-testid="lp-sample-poster"
            />
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              16:9, ideally ≥ 1280×720. Falls back to the og:image if blank.
            </p>
          </div>
        </div>
        <div className="mb-4">
          <Label htmlFor="lp-sample-caption">Caption</Label>
          <Input
            id="lp-sample-caption"
            value={sample.caption}
            onChange={(e) => setSample({ ...sample, caption: e.target.value })}
            placeholder="What real buyers + breeders see in a 15-minute video tour."
            maxLength={240}
            data-testid="lp-sample-caption"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={saveSample}
            disabled={sampleSaving}
            className="rounded-full bg-[#C1272D] hover:bg-[#0E0E0E] text-white"
            data-testid="lp-sample-save"
          >
            {sampleSaving ? "Saving…" : "Save"}
          </Button>
          <Link
            to="/video-tours"
            target="_blank"
            className="text-[12.5px] text-crimson font-bold hover:underline inline-flex items-center gap-1"
            data-testid="lp-sample-preview"
          >
            <ArrowSquareOut size={11} weight="bold" /> Preview /video-tours
          </Link>
        </div>
      </Section>
    </AppShell>
  );
}

function SeoRow({ label, value, link = false }) {
  return (
    <div>
      <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#8A6A00] mb-0.5">
        {label}
      </p>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-ink hover:text-crimson underline-offset-2 hover:underline break-all">
          {value}
        </a>
      ) : (
        <p className="text-ink break-all">{value}</p>
      )}
    </div>
  );
}
